<template>
    <div :class="['pt-0', cssWidthOverride ? cssWidthOverride : 'content']">
        <div v-if="hasHeaderSlot()" class="px-4 pb-4 pt-1" >
            <slot name="header" :value="content.items" />
        </div>

        <div class="overflow-visible shadow-md md:rounded-lg text-gray-800">
            <table class="min-w-full divide-y divide-gray-300 rounded-lg">
                <thead v-if="hasTableHeader" class="bg-gray-50 rounded-lg">
                    <tr>
                        <th v-for="(field, idx) in tableHeaders" scope="col" :key="idx"  class="py-2 pl-4 text-left text-sm font-semibold text-white bg-midea-primary"
                        :class="{ 'rounded-tl-lg': idx === 0, 'rounded-tr-lg': filterable ? false : idx === tableHeaders.length - 1 }">
                            <div class="relative w-fit" @click="sortByField(field)">
                                {{field}}
                            </div>
                        </th>
                        <th v-if="tableEditVisible" scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span class="sr-only">Details</span>
                        </th>
                        <th v-if="allowFilter" class="relative py-2 px-4 text-left text-sm font-semibold bg-midea-primary rounded-tr-lg">
                            <FilterIcon v-show="!activeFilter" @click="activeFilter = true" class="h-5 text-white cursor-pointer"/>
                            <FilterIconSolid v-show="activeFilter" @click="activeFilter = false" class="h-5 text-white cursor-pointer"/>
                            <div ref="filterContainer">
                                <transition
                                    enter-active-class="transition duration-200 ease-out"
                                    enter-from-class="-translate-y-1 translate-x-1 opacity-0"
                                    enter-to-class="translate-y-0 translate-x-0 opacity-100"
                                    leave-active-class="transition duration-150 ease-in"
                                    leave-from-class="translate-y-0 translate-x-0 opacity-100"
                                    leave-to-class="-translate-y-1 translate-x-1 opacity-0"
                                >
                                    <div v-if="activeFilter" class="absolute px-4 py-2 right-0 top-6">
                                        <slot name="filter" />
                                    </div>
                                </transition>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white">
                    <tr v-for="(item, itemIdx) in content.items" :key="itemIdx" class="group h-16" 
                        :class="{ 'bg-gray-50': (itemIdx) % 2 === 0,
                                  'cursor-pointer hover:bg-gray-100': props.onClickRow,
                                  'rounded-b-lg': hidingPagination
                                } " 
                        @click="selectRow(item)">
                        <slot name="row" :value="{ item, itemIdx }" v-if="!hasTableHeader || keepHeaderWithSlots"></slot>
                        <td v-if="tableEditVisible" class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button @click.stop="select(item)" class="text-blue-900 hover:text-blue-800"
                                >Details<span class="sr-only">, {{ item.name }}</span></button>
                        </td>
                        <td
                            v-if="allowFilter"
                            class="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 -z-10"
                            :class="{ 'rounded-br-lg': hidingPagination }"
                        >
                        </td>
                    </tr>
                </tbody>
                <tfoot class="bg-white" v-if="!hidingPagination">
                    <tr class=""> 
                        <!-- tableKeys length = 0 when there are no keys, but details takes up a slot -->
                        <td :colspan="(tableKeys.length || 1) + 1" class="rounded-b-lg">
                            <Pagination :currentPage="content.page" :pagination="content" :gotoPage="gotoPage"/>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, watch, computed, useSlots, defineProps, defineExpose } from 'vue'
    import Pagination from './DataTablePagination.vue'
    import { FilterIcon } from '@heroicons/vue/outline'
    import { FilterIcon as FilterIconSolid } from '@heroicons/vue/solid'
    import { onClickOutside } from '@vueuse/core'

    const slots = useSlots()

    const props = defineProps([
      'title', 'page', 'search', 'singularTitle', 'fields', 'debugPrintData', 'noCreate', 'tableEditVisible', 'keepHeaderWithSlots',
      'allowSort', 'defaultSort', 'cssWidthOverride', 'onSetPage', 'onUpdateData', 'onSelectItem', 'onClickRow', 'onSearch', 'allowFilter', 'hidePagination'
    ])

    defineExpose({ requestRefresh })

    const content = ref({ page: 0, pages: 0, limit: 0, count: 0, items: [] })
    const page = ref(0)
    const currentSort = ref()
    const activeFilter = ref(false)
    const filterContainer = ref()

    const hasTableHeader = computed(() => props.fields != null)
    const tableKeys = computed(() => Object.keys(props.fields || {}))
    const tableHeaders = computed(() => {
        const p = props.fields
        return tableKeys.value.map(a => !p || p[a] === true ? a : p[a])
    })

    const filterable = computed(() => props.allowFilter)
    const hidingPagination = computed(() => props.hidePagination)

    onMounted(async () => {
        if (props.search) {
            searchVal.value = props.search
        }

        if (props.allowSort && props.defaultSort) {
            currentSort.value = props.defaultSort
        }

        onClickOutside(filterContainer, (clickEvent) => {
            activeFilter.value = false
            if (clickEvent.target.closest('div')?.id === 'filterFeedContainer') {
                clickEvent.stopPropagation()
            }
        })

        await updateData()
    })

    watch(props, updateData)

    async function updateData() {
        if (props.page !== undefined) {
            content.value = await props.onUpdateData(props.page, currentSort.value)
        }
        else {
            content.value = await props.onUpdateData(page.value || 0, currentSort.value)
        }
    }

    function requestRefresh() {
        page.value = 0
        updateData()
    }

    function select(item) {
        if (item.id)
        {
            props.onSelectItem(item.id)
        }
        else 
        {
            props.onSelectItem(item)
        }
    }

    function selectRow(item) {
        if (!props.onClickRow) { return }

        if (item.id)
        {
            props.onClickRow(item.id)
        }
        else 
        {
            props.onClickRow(item)
        }
    }

    function gotoPage(idx) {
        if (idx == null) return
        if (idx < 0) { idx = content.value.pages - 1 } // can negatively index in the future if we need to
        if (idx >= 0 && idx < content.value.pages) {
            if (props.onSetPage) {
                props.onSetPage(idx)
            }
            else {
                page.value = idx
                updateData()
            }
        }
    }

    const searchVal = ref('')
    
    function hasHeaderSlot() {
        return slots.header
    }

    function sortByField(field) {
        if (!hasTableHeader.value || !props.fields || !props.allowSort) return

        const fields = props.fields
        const fieldKey = tableKeys.value.find(tableKey => fields[tableKey] === field )
        if (!fieldKey) return

        if (currentSort.value && currentSort.value?.field === fieldKey) {
            currentSort.value.direction = currentSort.value?.direction === 'asc' ? 'desc' : 'asc'
        }
        else {
            currentSort.value = { field: fieldKey, direction: 'desc' }
        }

        requestRefresh()
    }
</script>